import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Pie, PieChart, Tooltip } from 'recharts';

import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

export const GROWTH_GROUPS: Array<{ label: MyHiveLocalizationKey; color: string; test: (h: string) => boolean }> = [
  { label: MyHiveLocalizationKey.EdnaGrowthTreesShrubs,  color: '#338a88', test: (h: string) => /tree|shrub|subshrub/i.test(h) },
  { label: MyHiveLocalizationKey.EdnaGrowthWildflowers,  color: '#e27341', test: (h: string) => /herb|forb|wildflower|annual|perennial/i.test(h) },
  { label: MyHiveLocalizationKey.EdnaGrowthVinesGrasses, color: '#4083ba', test: (h: string) => /vine|grass|graminoid|liana|sedge|rush|aquatic/i.test(h) },
  { label: MyHiveLocalizationKey.EdnaGrowthMixed,        color: '#c8c7bf', test: () => true },
];

interface GrowthGroup { label: MyHiveLocalizationKey; color: string; fill: string; value: number; pct: number; topPlants: string[] }

export function GrowthTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: GrowthGroup }> }): React.JSX.Element | null {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  if (!active || !payload?.length) return null;
  const g = payload[0]?.payload;
  if (!g || g.value === 0) return null;
  return (
    <div
      className="rounded-xl px-4 py-3 text-left shadow-lg"
      style={{ backgroundColor: '#013331', border: 'none', minWidth: 180 }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: g.color }} />
        <span className="font-brand-md text-sm" style={{ color: 'rgba(255,255,255,0.95)' }}>{t[g.label]}</span>
      </div>
      <p className="font-brand-sm text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
        {g.pct}{t[MyHiveLocalizationKey.EdnaGrowthPctOfGroups]}
      </p>
      {g.topPlants.length > 0 && (
        <p className="font-brand-sm text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
          {g.topPlants.join(', ')}
        </p>
      )}
    </div>
  );
}

export interface Detection {
  growthHabit: string;
  commonNameEn: string;
  count: number;
}

export function useGrowthGroups(detections: Detection[]): GrowthGroup[] {
  return useMemo(() => {
    const buckets = GROWTH_GROUPS.map(g => ({
      ...g,
      count: 0,
      plants: [] as Array<{ name: string; hits: number }>,
    }));
    for (const d of detections) {
      const bucket = buckets.find(b => b.test(d.growthHabit));
      if (bucket) {
        bucket.count++;
        bucket.plants.push({ name: d.commonNameEn, hits: d.count });
      }
    }
    const total = buckets.reduce((s, b) => s + b.count, 0);
    return buckets.map(b => ({
      label: b.label,
      color: b.color,
      fill: b.color,
      value: b.count,
      pct: total > 0 ? Math.round((b.count / total) * 100) : 0,
      topPlants: b.plants.sort((a, z) => z.hits - a.hits).slice(0, 4).map(p => p.name),
    }));
  }, [detections]);
}

interface GrowthDonutChartProps {
  detections: Detection[];
  totalUniqueHits?: number | null;
  chartSize?: number;
  noText?: boolean;
}

export function GrowthDonutChart({ detections, totalUniqueHits, chartSize: chartSizeProp = 200, noText = false }: GrowthDonutChartProps): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const growthGroups = useGrowthGroups(detections);
  const legendRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState(chartSizeProp);

  useEffect(() => {
    if (!legendRef.current) return;
    const h = legendRef.current.offsetHeight;
    if (h > 0) setChartSize(h);
  }, [growthGroups]);

  return (
    <div className="flex items-center gap-6 w-full">
      {/* Chart */}
      <div className="relative shrink-0" style={{ width: chartSize, height: chartSize }}>
        <PieChart width={chartSize} height={chartSize}>
          <Pie
            data={growthGroups.filter(g => g.value > 0)}
            dataKey="value"
            cx="50%"
            cy="50%"
            innerRadius="50%"
            outerRadius="100%"
            startAngle={90}
            endAngle={-270}
            stroke="none"
            isAnimationActive={false}
          />
          <Tooltip
            content={<GrowthTooltip />}
            wrapperStyle={{ zIndex: 10 }}
            allowEscapeViewBox={{ x: true, y: true }}
            isAnimationActive={false}
          />
        </PieChart>
        {!noText && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="font-brand-2xl text-3xl text-gray-900 tabular-nums leading-none">{totalUniqueHits ?? '—'}</p>
            <p className="font-brand-sm text-[10px] text-gray-400 mt-1 uppercase tracking-widest text-center leading-tight" style={{ whiteSpace: 'pre-line' }}>{t[MyHiveLocalizationKey.EdnaGrowthKindsByForm]}</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div ref={legendRef} className="flex flex-col gap-1.5 flex-1 min-w-0">
        {growthGroups.map(g => (
          <div key={g.label} className="flex items-center gap-2.5 rounded-lg px-3 py-2" style={{ backgroundColor: '#f3f4f6' }}>
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
            <span className="font-brand-md text-sm text-gray-700 flex-1 min-w-0 leading-tight">{t[g.label]}</span>
            <span className="font-brand-sm text-sm text-gray-500 tabular-nums shrink-0">
              {g.value > 0 ? `${g.pct}%` : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
